// Facade API for Section 1.3 – Description of facade system
// Uses backend if available, falls back to localStorage for persistence.

const isDev = process.env.NODE_ENV === 'development';
const canProxy = typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '3001');
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  ((isDev && canProxy)
    ? ''
    : (typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.hostname}:8000`
        : 'http://127.0.0.1:8000'));

const lsKey = (buildingId) => `facade_system_${buildingId}`;

const defaultPayload = () => ({
  rows: [{ elevation: 'Front', type: '', connection: '', notes: '' }],
});

export async function getFacadeSystem(buildingId) {
  if (!buildingId) return defaultPayload();

  // Try backend first
  try {
    const res = await fetch(`${API_BASE_URL}/api/buildings/${buildingId}/facade-system`, {
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
    });
    if (res.ok) {
      const data = await res.json();
      // Expect shape: { rows: [{ elevation, type, connection, notes }] }
      return data?.rows && Array.isArray(data.rows) ? data : defaultPayload();
    }
  } catch (e) {
    // ignore and fall back
  }

  // Fallback to localStorage
  try {
    const raw = localStorage.getItem(lsKey(buildingId));
    return raw ? JSON.parse(raw) : defaultPayload();
  } catch {
    return defaultPayload();
  }
}

export async function saveFacadeSystem(buildingId, payload) {
  if (!buildingId) return { success: false };

  // Attempt backend save (POST create or PUT update)
  try {
    const res = await fetch(`${API_BASE_URL}/api/buildings/${buildingId}/facade-system`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || defaultPayload()),
    });
    // Even if backend fails, we still persist locally below
    if (res.ok) {
      const data = await res.json();
      // Also mirror to localStorage for offline continuity
      try { localStorage.setItem(lsKey(buildingId), JSON.stringify(data)); } catch {}
      return { success: true, data };
    }
  } catch (e) {
    // ignore and persist locally
  }

  // Persist to localStorage as fallback
  try {
    const sanitized = {
      rows: (payload?.rows || []).map((r) => ({
        elevation: r.elevation || '',
        type: r.type || '',
        connection: r.connection || '',
        notes: r.notes || '',
      })),
    };
    localStorage.setItem(lsKey(buildingId), JSON.stringify(sanitized));
    return { success: true, data: sanitized };
  } catch (e) {
    return { success: false, error: e?.message || 'Failed to save facade system' };
  }
}